import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconExternalLink, IconFlask } from "@tabler/icons-react";
import type { AlphaGroupListItem } from "@smartwiz/shared-types";
import {
  listAlphaGroups,
  enrollInAlpha,
  unenrollFromAlpha,
} from "@/pages/AlphaSignup/api";

const AlphaPrograms = (): JSX.Element => {
  const [alphaGroups, setAlphaGroups] = useState<AlphaGroupListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionSlug, setActionSlug] = useState<string | null>(null);

  const fetchGroups = async () => {
    try {
      const data = await listAlphaGroups();
      setAlphaGroups(data.alphaGroups);
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to load alpha programs",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleJoin = async (slug: string) => {
    setActionSlug(slug);
    try {
      await enrollInAlpha(slug);
      showNotification({
        title: "Enrolled!",
        message: "You've joined the alpha program",
        color: "green",
      });
      await fetchGroups();
    } catch (e: any) {
      showNotification({
        title: "Error",
        message: e?.response?.data?.message || "Failed to enroll",
        color: "red",
      });
    } finally {
      setActionSlug(null);
    }
  };

  const handleLeave = async (slug: string) => {
    setActionSlug(slug);
    try {
      await unenrollFromAlpha(slug);
      showNotification({
        title: "Unenrolled",
        message: "You've left the alpha program",
        color: "blue",
      });
      await fetchGroups();
    } catch (e: any) {
      showNotification({
        title: "Error",
        message: e?.response?.data?.message || "Failed to unenroll",
        color: "red",
      });
    } finally {
      setActionSlug(null);
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  if (alphaGroups.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <IconFlask size={48} color="gray" />
          <Text c="dimmed">No alpha programs available right now.</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {alphaGroups.map((group) => {
        const startDate = new Date(
          group.startDate + "T00:00:00"
        ).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });

        return (
          <Card key={group.slug} withBorder radius="md" p="lg">
            <Group justify="space-between" align="center">
              <Stack gap={4}>
                <Group gap="sm">
                  <Text fw={600} size="md">
                    {group.name}
                  </Text>
                  {group.enrollmentStatus === "active" && (
                    <Badge color="green" variant="light" size="sm">
                      Enrolled
                    </Badge>
                  )}
                  {group.enrollmentStatus === "waitlisted" && (
                    <Badge color="orange" variant="light" size="sm">
                      Waitlisted
                    </Badge>
                  )}
                  {!group.hasStarted && !group.enrollmentStatus && (
                    <Badge color="blue" variant="light" size="sm">
                      Starts {startDate}
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  <Text c="dimmed" size="sm">
                    {group.description}
                  </Text>
                  <Text
                    component="a"
                    href={`/alpha/${group.slug}`}
                    target="_blank"
                    size="sm"
                    c="blue"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Learn more <IconExternalLink size={14} />
                  </Text>
                </Group>
              </Stack>
              {group.enrollmentStatus ? (
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  loading={actionSlug === group.slug}
                  onClick={() => handleLeave(group.slug)}
                >
                  Leave
                </Button>
              ) : (
                <Button
                  variant="filled"
                  size="sm"
                  loading={actionSlug === group.slug}
                  onClick={() => handleJoin(group.slug)}
                >
                  {group.hasStarted ? "Join" : "Join Waitlist"}
                </Button>
              )}
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
};

export default AlphaPrograms;
